// Session UI 状态：选中哪台 PC、当前 message stream、连接状态等。
import { signal } from "@preact/signals";
import type { Envelope, WSState } from "../transport/ws";
import type { DirEntry, SessionEntry } from "../transport/cli";
import { markAgentOffline, markAgentOnline } from "./agents";

export const selectedAgentId = signal<string | null>(null);
export const wsState = signal<WSState>("idle");
export const wsStateDetail = signal<string>("");
export const wsLog = signal<string[]>([]); // 调试用，最多 50 行

// Day-8: 项目目录 + session 续接
export const dirsByAgent = signal<Record<string, DirEntry[]>>({});  // agent_id → dirs
export const sessionsByCwd = signal<Record<string, SessionEntry[]>>({}); // cwd → sessions
export const selectedCwd = signal<string>("");                       // 当前选中的项目目录
export const selectedResumeId = signal<string>("");                  // 选了"续接 X session" → session_id
export const continueRecent = signal<boolean>(false);                // 用 -c 续接最近一次

// Day-9: 多轮对话 — 自动跟踪当前 claude_session_id，下一条消息默认续接它
export const currentClaudeSessionId = signal<string>("");
// Day-9: 中止按钮 — 当前正在流式的 phone session_id（非空时显示 ⏹）
export const streamingSessionId = signal<string>("");
// Day-9: 中止后 ack 回来前的 pending 状态
export const killPending = signal<boolean>(false);

export interface ChatMsg {
  role: "user" | "assistant" | "system";
  text: string;
  ts: number;
  /** server message_id，仅 assistant 流式消息有 */
  messageId?: string;
  streaming?: boolean;
}
export const chat = signal<ChatMsg[]>([]);

export function pushLog(line: string) {
  const next = [...wsLog.value, `${new Date().toLocaleTimeString()} ${line}`];
  if (next.length > 50) next.shift();
  wsLog.value = next;
}

export function pushChat(m: ChatMsg) {
  chat.value = [...chat.value, m];
}

export function appendDelta(messageId: string, deltaText: string) {
  const list = [...chat.value];
  const last = list[list.length - 1];
  if (last && last.role === "assistant" && last.streaming && last.messageId === messageId) {
    last.text += deltaText;
    chat.value = list;
  } else {
    pushChat({
      role: "assistant",
      text: deltaText,
      ts: Date.now(),
      messageId,
      streaming: true
    });
  }
}

export function finalizeMessage(messageId: string) {
  const list = [...chat.value];
  for (const m of list) {
    if (m.messageId === messageId) m.streaming = false;
  }
  chat.value = list;
}

export function handleEnvelope(env: Envelope) {
  pushLog(`← ${env.type}`);
  if (env.type === "command_started") {
    // Day-9: 记录 agent 启动后告诉我们的 claude_session_id，下条消息默认续接
    const p = env.payload as { claude_session_id?: string };
    if (p.claude_session_id) {
      currentClaudeSessionId.value = p.claude_session_id;
    }
  } else if (env.type === "assistant_delta") {
    const p = env.payload as { message_id: string; delta_text: string };
    appendDelta(p.message_id, p.delta_text);
  } else if (env.type === "assistant_done") {
    const p = env.payload as { message_id: string; session_id?: string; finish_reason?: string };
    finalizeMessage(p.message_id);
    // Day-9: 流式结束 → 清 streaming 状态（恢复输入框 + 把 ⏹ 换回 ↑）
    if (p.session_id && streamingSessionId.value === p.session_id) {
      streamingSessionId.value = "";
      killPending.value = false;
    }
    if (p.finish_reason && p.finish_reason !== "stop" && p.finish_reason !== "end_turn") {
      pushChat({
        role: "system",
        text: `[done] finish_reason=${p.finish_reason}`,
        ts: Date.now(),
      });
    }
  } else if (env.type === "assistant_error") {
    const p = env.payload as { code: string; msg: string };
    pushChat({
      role: "system",
      text: `[agent error ${p.code}] ${p.msg}`,
      ts: Date.now(),
    });
  } else if (env.type === "ack") {
    // Day-9: cmd_kill_cc 的 ack 回来 → 关掉 pending（done 才清 streaming）
    const p = env.payload as { ack_id: string; status: string };
    if (killPending.value && p.status === "ok") {
      pushChat({
        role: "system",
        text: "🟡 已发送中断信号，等待 claude 退出…",
        ts: Date.now(),
      });
    } else if (killPending.value && p.status === "error") {
      pushChat({
        role: "system",
        text: "❌ 中断失败（任务可能已结束）",
        ts: Date.now(),
      });
      killPending.value = false;
    }
  } else if (env.type === "agent_offline") {
    const p = env.payload as { agent_id: string; alias?: string };
    markAgentOffline(p.agent_id);
    pushChat({
      role: "system",
      text: `🔴 ${p.alias ?? p.agent_id} 离线（90s 无心跳）`,
      ts: Date.now(),
    });
  } else if (env.type === "agent_online") {
    const p = env.payload as { agent_id: string; alias?: string };
    markAgentOnline(p.agent_id);
    pushChat({
      role: "system",
      text: `🟢 ${p.alias ?? p.agent_id} 已重连`,
      ts: Date.now(),
    });
  } else if (env.type === "error") {
    pushChat({
      role: "system",
      text: `[error] ${JSON.stringify(env.payload)}`,
      ts: Date.now()
    });
  }
}
